import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { cognitoAuth } from '@/services/auth/cognito';
import { useAuthStore } from '@/store/authStore';
import { usersService } from '@/services/api/users';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { CookieConsent } from '@/components/shared/CookieConsent';
import logoImg from '@/assets/img/logo.png';
import displayImg from '@/assets/img/display.png';

// Google icon SVG component
const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export function LoginPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  // Force dark mode on login page
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  // If already authenticated, redirect to panel
  useEffect(() => {
    if (isAuthenticated) {
      const returnTo = sessionStorage.getItem('login-return-to') || '/panel';
      sessionStorage.removeItem('login-return-to');
      window.location.href = returnTo;
    }
  }, [isAuthenticated]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await cognitoAuth.signIn(email, password);

      if (result.requiresMFA) {
        setShowMfa(true);
        setIsLoading(false);
        return;
      }

      if (!result.success) {
        setError(result.error || 'Login failed');
        setIsLoading(false);
        return;
      }

      await handleSuccessfulAuth();
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setError('');

    try {
      // Google OAuth via Cognito Hosted UI
      const result = await cognitoAuth.signInWithGoogle();
      if (!result.success) {
        setError(result.error || 'Google login failed');
      }
      // If successful, the user will be redirected to Google
    } catch (err) {
      setError('Failed to initiate Google login');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await cognitoAuth.confirmMFA(mfaCode);

      if (!result.success) {
        setError(result.error || 'Invalid MFA code');
        setIsLoading(false);
        return;
      }

      await handleSuccessfulAuth();
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessfulAuth = async () => {
    const userInfo = await cognitoAuth.getUserInfo();
    const token = await cognitoAuth.getIdToken();

    if (userInfo && token) {
      // Set token in store so the API interceptor can use it
      useAuthStore.setState({ token });

      // Build user object from API or fallback to Cognito
      let userObj: any;
      let tenantModules: any = null;
      let tenantPlan = 'trial';

      try {
        const userData = await usersService.getCurrentUser();
        if (userData && userData.id) {
          userObj = {
            id: userData.id,
            cognitoUserId: userData.cognitoUserId || userInfo.userId,
            tenantId: userData.tenantId || userInfo.tenantId || '',
            email: userData.email,
            firstName: userData.firstName || userInfo.givenName || null,
            lastName: userData.lastName || userInfo.familyName || null,
            role: userData.role || (userInfo.role as any) || 'user',
            status: userData.status || 'active',
            permissions: userData.permissions || {},
            lastLoginAt: userData.lastLoginAt || new Date().toISOString(),
            createdAt: userData.createdAt || '',
            updatedAt: userData.updatedAt || '',
            createdBy: null,
            onboardingCompleted: (userData as any).onboardingCompleted || false,
          };
          tenantModules = (userData as any).tenantModules || null;
          tenantPlan = (userData as any).tenantPlan || 'trial';
        }
      } catch (err: any) {
        // If API returns 401/403, user exists in Cognito but not authorized in the system
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          await cognitoAuth.signOut();
          useAuthStore.setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
          setError('Your account is not authorized to access this system. Please contact your administrator.');
          setIsLoading(false);
          return;
        }
        // For other API errors (network issues, 500, etc.), fall through to use Cognito data
      }

      if (!userObj) {
        userObj = {
          id: userInfo.userId,
          cognitoUserId: userInfo.userId,
          tenantId: userInfo.tenantId || '',
          email: userInfo.email,
          firstName: userInfo.givenName || null,
          lastName: userInfo.familyName || null,
          role: (userInfo.role as any) || 'user',
          status: 'active',
          permissions: {},
          lastLoginAt: new Date().toISOString(),
          createdAt: '',
          updatedAt: '',
          createdBy: null,
        };
      }

      // Set ALL auth state in ONE atomic Zustand call
      useAuthStore.setState({
        user: userObj,
        token,
        isAuthenticated: true,
        tenantModules,
        tenantPlan,
      });

      // Redirect to where the user was trying to go, or default to /panel
      const returnTo = sessionStorage.getItem('login-return-to') || '/panel';
      sessionStorage.removeItem('login-return-to');
      window.location.href = returnTo;
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[60%_40%] bg-[#282836]">
      {/* Left side - Login Form */}
      <div className="flex flex-col justify-center px-6 py-12 lg:px-12 xl:px-20">
        <div className="mx-auto w-full max-w-[400px] space-y-8">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src={logoImg} alt="Roni AI" className="h-10 w-auto" />
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-white">
              {showMfa ? 'Two-Factor Authentication' : 'Welcome to RoniDoc AI'}
            </h1>
            <p className="text-[15px] text-zinc-400">
              {showMfa
                ? 'Enter the verification code from your authenticator app'
                : 'Sign in to your documents management panel'
              }
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}

          {!showMfa ? (
            <>
              {/* Login Form */}
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-zinc-300">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-12 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary focus:ring-primary/20"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium text-zinc-300">
                      Password
                    </Label>
                    <Button
                      type="button"
                      variant="link"
                      className="px-0 text-xs font-normal h-auto text-primary hover:text-primary/80"
                      onClick={() => navigate('/forgot-password')}
                    >
                      Forgot password?
                    </Button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      className="h-12 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary focus:ring-primary/20 pr-12"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-12 w-12 text-zinc-400 hover:text-white hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-medium text-[15px] shadow-lg shadow-primary/20"
                  disabled={isLoading}
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sign in
                </Button>
              </form>

              {/* Legal Text */}
              <p className="text-center text-[13px] text-zinc-500 leading-relaxed">
                By signing in to Roni AI, you agree to our{' '}
                <Link to="/terms" className="text-primary hover:underline">
                  Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
                .
              </p>
              <p className="text-center text-sm text-zinc-400 mt-4">
                Don't have an account?{' '}
                <Link to="/signup" className="text-primary hover:underline font-medium">Sign up</Link>
              </p>
            </>
          ) : (
            /* MFA Form */
            <form onSubmit={handleMfaSubmit} className="space-y-5">
              <div className="flex justify-center mb-6">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                  <ShieldCheck className="h-10 w-10 text-primary" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mfaCode" className="text-sm font-medium text-zinc-300">
                  Verification Code
                </Label>
                <Input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  required
                  disabled={isLoading}
                  className="h-16 text-center text-2xl tracking-[0.5em] font-mono bg-zinc-900/50 border-zinc-800 text-white"
                  autoComplete="one-time-code"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-medium"
                disabled={isLoading || mfaCode.length < 6}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-zinc-400 hover:text-white hover:bg-zinc-800"
                onClick={() => {
                  setShowMfa(false);
                  setMfaCode('');
                  setError('');
                }}
              >
                Back to login
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* Right side - Image/Branding */}
      <div className="hidden lg:flex flex-col relative overflow-hidden bg-[#282836]">
        {/* Gradient orbs background */}
        <div className="absolute top-1/4 -right-20 w-96 h-96 bg-purple-600/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -left-20 w-80 h-80 bg-blue-600/20 rounded-full blur-[100px]" />

        {/* Spacer to push content down */}
        <div className="flex-1" />

        {/* Slogan above image */}
        <div className="relative z-10 px-12 text-left mb-8">
          <h2 className="text-5xl font-bold text-white tracking-tight mb-4 leading-tight">
            Your Documents<br />
            Your Questions<br />
            Instant Answers
          </h2>
          <p className="text-zinc-400 text-lg max-w-md">
            Upload, search, and chat with your documents using <span className="whitespace-nowrap">AI-powered</span> intelligence.
          </p>
        </div>

        {/* Display image at bottom right */}
        <div className="relative z-10 self-end pr-0 pb-0">
          {/* Glow behind image */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 via-primary/40 to-blue-500/30 rounded-tl-3xl blur-3xl transform scale-110 opacity-70" />

          {/* Image - no background, no resize, just glow */}
          <img
            src={displayImg}
            alt="RoniDoc AI"
            className="relative drop-shadow-[0_0_40px_rgba(139,92,246,0.4)]"
          />
        </div>
      </div>

      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
}
